// composables/useCounselorList.js

// 모듈 레벨 캐시 (페이지 이동에 영향 안 받음)
let pendingListPromise = null;
let cachedList = [];
let listLoaded = false;
let detailCache = {};          // { cslKey: detailObject }
let loadingDetails = {};       // { cslKey: true }

export const useCounselorList = () => {
  const { api } = useApi();

  // 반응성 트리거용 (화면 업데이트 위해)
  const listVersion = useState("counselorListVersion", () => 0);
  const detailVersion = useState("counselorDetailVersion", () => 0);
  const isLoading = useState("counselorListLoading", () => false);

  /**
   * 상담사 전체 목록 조회 (앱 생명주기 동안 1회)
   * forceRefresh: true 면 캐시가 있어도 서버에서 다시 받아 캐시를 갱신한다.
   *               이미 받는 중인 요청이 있으면 그 결과를 같이 쓴다.
   */
  const fetchCslList = async (forceRefresh = false) => {
    if (listLoaded && !forceRefresh) {
      // listVersion 참조해서 반응성 유지
      listVersion.value;
      return cachedList;
    }
    if (pendingListPromise) return pendingListPromise;

    pendingListPromise = (async () => {
      isLoading.value = true;
      try {
        const response = await api("/api/csl/readCsl", {
          pageNo: 0,
          pageSize: 1000,
        });
        cachedList = response.result.cslList ?? [];
        listLoaded = true;
        listVersion.value++; // 반응성 트리거
      } catch (error) {
        console.error("상담사 목록 조회 오류:", error);
      } finally {
        isLoading.value = false;
        pendingListPromise = null;
      }
      return cachedList;
    })();

    return pendingListPromise;
  };

  /**
   * cslList를 computed처럼 반환 (반응성 유지)
   */
  const cslList = computed(() => {
    listVersion.value; // 의존성 등록
    return cachedList;
  });

  const isLoaded = computed(() => {
    listVersion.value; // 의존성 등록
    return listLoaded;
  });

  /**
   * 특정 cslKey로 상담사 조회
   */
  const getCslByKey = (cslKey) => {
    return cachedList.find((csl) => csl.cslKey === cslKey) ?? null;
  };

  /**
   * 기본 정보 + 상세 정보 병합해서 반환
   */
  const getCslFullByKey = (cslKey) => {
    detailVersion.value; // 의존성 등록
    const base = getCslByKey(cslKey);
    const detail = detailCache[cslKey];
    if (!base && !detail) return null;
    return { ...(base ?? {}), ...(detail ?? {}) };
  };

  /**
   * 상담사 상세 정보 조회
   */
  const fetchCslDetail = async (cslKey, forceRefresh = false) => {
    const cachedDetail = detailCache[cslKey];

    if (!forceRefresh && cachedDetail) {      
      return getCslFullByKey(cslKey);
    }

    if (loadingDetails[cslKey]) {
      return await waitForDetail(cslKey);
    }

    loadingDetails[cslKey] = true;

    try {
      const response = await api("/api/csl/readCslInfo", { cslKey });
      const detail = response.result ?? {};

      detailCache[cslKey] = detail;
      detailVersion.value++; // 반응성 트리거
      
      return getCslFullByKey(cslKey);
    } catch (error) {
      console.error("상담사 상세 조회 오류:", error);
      return getCslByKey(cslKey);
    } finally {
      delete loadingDetails[cslKey];
    }
  };

  const waitForDetail = (cslKey) => {
    return new Promise((resolve) => {
      const check = () => {
        if (!loadingDetails[cslKey]) {
          resolve(getCslFullByKey(cslKey));
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  };

  /**
   * 특정 상담사 상세 캐시 무효화
   */
  const invalidateDetail = (cslKey) => {
    delete detailCache[cslKey];
    detailVersion.value++;
  };

  return {
    cslList,
    isLoaded,
    isLoading,
    fetchCslList,
    getCslByKey,
    getCslFullByKey,
    fetchCslDetail,
    invalidateDetail,
  };
};